(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.UCDHTVCustomizerCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  // HTV-ONLY pricing. These values are intentionally independent from
  // assets/ucd-customizer-core.js so decal pricing can never affect HTV.
  const BASE_PRICE_CENTS = Object.freeze({
    4: 575, 4.5: 634, 5: 692, 5.5: 751, 6: 809, 6.5: 868, 7: 926, 7.5: 985,
    8: 1043, 8.5: 1102, 9: 1160, 9.5: 1219, 10: 1277, 10.5: 1336, 11: 1394, 11.5: 1453,
  });

  // HTV-ONLY quantity savings. Shopify automatic discounts for the HTV
  // product must be configured separately to match these display tiers.
  const DISCOUNT_TIERS = Object.freeze([
    Object.freeze({ minimumQuantity: 2, percent: 10 }),
    Object.freeze({ minimumQuantity: 5, percent: 15 }),
    Object.freeze({ minimumQuantity: 10, percent: 20 }),
  ]);

  function maxLinesForSize(size) {
    return Number(size) < 6 ? 2 : 3;
  }

  function spacingBand(spacing) {
    const value = Number(spacing);
    if (value <= 1.2) return '0.9–1.2';
    if (value <= 1.4) return '1.3–1.4';
    return '1.5–1.6';
  }

  function layoutOption(lines, spacing) {
    const count = Number(lines);
    return count === 1 ? '1 Line' : `${count} Lines · Spacing ${spacingBand(spacing)}`;
  }

  function calculateUnitPriceCents(input) {
    const size = Number(input.size);
    const lines = Number(input.lines);
    const spacing = Number(input.spacing);
    if (!Object.prototype.hasOwnProperty.call(BASE_PRICE_CENTS, size)) throw new RangeError('Choose a size from 4 to 11.5 inches.');
    if (![1, 2, 3].includes(lines) || lines > maxLinesForSize(size)) throw new RangeError('Too many text lines for this HTV iron-on size.');
    if (spacing < 0.9 || spacing > 1.6 || Math.abs(spacing * 10 - Math.round(spacing * 10)) > 0.001) throw new RangeError('Choose line spacing from 0.9 to 1.6.');
    const lineCharge = (lines - 1) * 100;
    const spacingCharge = lines === 1 || spacing <= 1.2 ? 0 : spacing <= 1.4 ? 50 : 100;
    return BASE_PRICE_CENTS[size] + lineCharge + spacingCharge;
  }

  function discountForQuantity(quantity) {
    let percent = 0;
    DISCOUNT_TIERS.forEach((tier) => {
      if (quantity >= tier.minimumQuantity) percent = tier.percent;
    });
    return percent;
  }

  function calculateTotal(input) {
    const quantity = Number(input.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) throw new RangeError('Quantity must be at least 1.');
    const discountPercent = discountForQuantity(quantity);
    const unitCents = Number(input.unitCents);
    const discountPerUnitCents = Math.floor(unitCents * discountPercent / 100);
    return {
      discountPercent,
      totalCents: (unitCents - discountPerUnitCents) * quantity,
    };
  }

  function resolveVariantPriceCents(variant, fallbackCents) {
    const shopifyPrice = variant && Number(variant.price);
    return Number.isInteger(shopifyPrice) && shopifyPrice > 0 ? shopifyPrice : Number(fallbackCents);
  }

  function textFits(lines, size, widthFactor) {
    if (!Array.isArray(lines) || lines.length === 0 || lines.length > maxLinesForSize(size)) return false;
    const safeCharacterHeight = 0.5;
    return lines.every((line) => String(line).trim().length > 0 && String(line).trim().length * safeCharacterHeight * 0.6 * Number(widthFactor) <= Number(size));
  }

  function hueAndSaturation(colour) {
    const values = colour.map((channel) => channel / 255);
    const maximum = Math.max(...values);
    const minimum = Math.min(...values);
    const delta = maximum - minimum;
    if (delta === 0) return { hue: 0, saturation: 0 };
    let hue;
    if (maximum === values[0]) hue = ((values[1] - values[2]) / delta) % 6;
    else if (maximum === values[1]) hue = (values[2] - values[0]) / delta + 2;
    else hue = (values[0] - values[1]) / delta + 4;
    hue = (hue * 60 + 360) % 360;
    const lightness = (maximum + minimum) / 2;
    const saturation = delta / (1 - Math.abs(2 * lightness - 1));
    return { hue, saturation };
  }

  function countRasterColours(data) {
    const samples = [];
    let hasNonWhite = false;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 64) continue;
      const colour = [data[i], data[i + 1], data[i + 2]];
      const isNearWhite = colour[0] > 245 && colour[1] > 245 && colour[2] > 245;
      if (!isNearWhite) hasNonWhite = true;
      samples.push({ colour, isNearWhite });
    }
    const hueClusters = [];
    let hasGreyscale = false;
    samples.forEach(({ colour, isNearWhite }) => {
      if (hasNonWhite && isNearWhite) return;
      const sample = hueAndSaturation(colour);
      if (sample.saturation < 0.15) {
        hasGreyscale = true;
        return;
      }
      const cluster = hueClusters.find((candidate) => {
        const difference = Math.abs(candidate - sample.hue);
        return Math.min(difference, 360 - difference) < 25;
      });
      if (cluster === undefined) hueClusters.push(sample.hue);
    });
    return hueClusters.length + (hasGreyscale ? 1 : 0);
  }

  function countSvgColours(svgText) {
    const text = String(svgText);
    if (/<(?:linearGradient|radialGradient|pattern)\b/i.test(text) || /url\s*\(/i.test(text)) return 2;
    const matches = [...text.matchAll(/(?:fill|stroke)\s*(?:=|:)\s*["']?([^;"'\s>]+)/gi)]
      .map((match) => match[1].toLowerCase())
      .filter((value) => !['none', 'transparent', 'currentcolor'].includes(value));
    const unique = [...new Set(matches)];
    const withoutWhite = unique.filter((value) => !['white', '#fff', '#ffffff', 'rgb(255,255,255)'].includes(value.replace(/\s/g, '')));
    return withoutWhite.length ? withoutWhite.length : unique.length;
  }

  function sizeOption(size) {
    return `${Number(size)} in`;
  }

  function findVariant(variants, size, lines, spacing) {
    const wanted = [sizeOption(size), 'Standard', layoutOption(lines, spacing)];
    return variants.find((variant) => variant.options && variant.options.every((option, index) => option === wanted[index])) || null;
  }

  return {
    BASE_PRICE_CENTS,
    DISCOUNT_TIERS,
    calculateUnitPriceCents,
    calculateTotal,
    resolveVariantPriceCents,
    maxLinesForSize,
    spacingBand,
    layoutOption,
    textFits,
    countRasterColours,
    countSvgColours,
    sizeOption,
    findVariant,
  };
});
